import React, { useState } from 'react';
import { ethers } from 'ethers';

// Minimal ERC721 ABI with only the functions we need
const ERC721_ABI = [
  // Get the owner of a specific token
  {
    "inputs": [{"internalType": "uint256", "name": "tokenId", "type": "uint256"}],
    "name": "ownerOf",
    "outputs": [{"internalType": "address", "name": "", "type": "address"}],
    "stateMutability": "view",
    "type": "function"
  },
  // Get total supply (if available)
  {
    "inputs": [],
    "name": "totalSupply",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  // Name of token (optional)
  {
    "inputs": [],
    "name": "name",
    "outputs": [{"internalType": "string", "name": "", "type": "string"}],
    "stateMutability": "view",
    "type": "function"
  },
  // Symbol of token (optional)
  {
    "inputs": [],
    "name": "symbol",
    "outputs": [{"internalType": "string", "name": "", "type": "string"}],
    "stateMutability": "view",
    "type": "function"
  }
];

// MegaETH RPC URL
const MEGAETH_RPC_URL = "https://carrot.megaeth.com/rpc";

export default function MegaETHHoldersTool() {
  const [contractAddress, setContractAddress] = useState('');
  const [startTokenId, setStartTokenId] = useState(0);
  const [endTokenId, setEndTokenId] = useState(999);
  const [batchSize, setBatchSize] = useState(25); // Smaller batch size for reliability
  const [retryDelay, setRetryDelay] = useState(500); // Delay between attempts in ms
  const [maxRetries, setMaxRetries] = useState(3); // Number of retries per token
  const [holders, setHolders] = useState({});
  const [tokenOwners, setTokenOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [contractInfo, setContractInfo] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [processLog, setProcessLog] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Connect to MegaETH and get contract instance
  const connectAndGetContract = async () => {
    try {
      // Create a provider with retry logic
      const provider = new ethers.JsonRpcProvider(MEGAETH_RPC_URL, undefined, {
        polling: true,
        pollingInterval: 1000,
        maxRetries: 5,
        retryDelay: 1000,
      });
      
      const network = await provider.getNetwork();
      addToLog(`Connected to network: ${network.name} (${network.chainId})`);
      
      return new ethers.Contract(contractAddress, ERC721_ABI, provider);
    } catch (error) {
      console.error("Connection error:", error);
      throw new Error("Failed to connect to MegaETH. Please check your internet connection and try again.");
    }
  };

  // Add message to process log
  const addToLog = (message) => {
    setProcessLog(prevLog => [...prevLog, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  // Get contract info (name, symbol, totalSupply)
  const getContractInfo = async (contract) => {
    try {
      let info = { address: contractAddress };
      
      try {
        info.name = await contract.name();
        addToLog(`Contract name: ${info.name}`);
      } catch (e) {
        info.name = "Unknown Collection";
        addToLog("Could not retrieve contract name");
      }
      
      try {
        info.symbol = await contract.symbol();
        addToLog(`Contract symbol: ${info.symbol}`);
      } catch (e) {
        info.symbol = "NFT";
        addToLog("Could not retrieve contract symbol");
      }
      
      try {
        const totalSupply = await contract.totalSupply();
        info.totalSupply = Number(totalSupply);
        addToLog(`Contract total supply: ${info.totalSupply}`);
        
        // Only update end token ID if we successfully got total supply
        if (info.totalSupply > 0) {
          setEndTokenId(info.totalSupply - 1);
        }
      } catch (e) {
        info.totalSupply = null;
        addToLog("Could not retrieve contract total supply");
      }
      
      return info;
    } catch (error) {
      console.error("Error getting contract info:", error);
      addToLog(`Error getting contract info: ${error.message}`);
      return { address: contractAddress, name: "Unknown Collection", symbol: "NFT", totalSupply: null };
    }
  };

  // Get owner of a specific token ID with retries
  const getOwnerWithRetry = async (contract, tokenId) => {
    let lastError = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const owner = await contract.ownerOf(tokenId);
        if (attempt > 0) {
          addToLog(`Retrieved owner for token ${tokenId} after ${attempt + 1} attempts`);
        }
        return { tokenId, owner, success: true };
      } catch (error) {
        lastError = error;
        
        // Check if error indicates token doesn't exist
        if (error.message.includes("nonexistent token") || 
            error.message.includes("owner query for nonexistent") || 
            error.message.includes("token doesn't exist") ||
            error.message.includes("invalid token ID")) {
          return { tokenId, owner: null, success: false, reason: "Token doesn't exist" };
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    addToLog(`Failed to get owner for token ${tokenId} after ${maxRetries} attempts: ${lastError.message}`);
    return { tokenId, owner: null, success: false, reason: lastError.message };
  };

  // Process tokens in batches with more reliable handling
  const processTokensInBatches = async (contract) => {
    const newHolders = {};
    const newTokenOwners = [];
    const totalTokens = endTokenId - startTokenId + 1;
    let processedCount = 0;
    let successCount = 0;
    let failedCount = 0;
    
    addToLog(`Starting to process tokens from ${startTokenId} to ${endTokenId}`);
    addToLog(`Using batch size of ${batchSize} with ${maxRetries} retries and ${retryDelay}ms delay`);
    
    for (let i = startTokenId; i <= endTokenId; i += batchSize) {
      const batchEndIndex = Math.min(i + batchSize - 1, endTokenId);
      addToLog(`Processing batch: tokens ${i} to ${batchEndIndex}`);
      
      // Create a batch of promises for this range of tokens
      const batchPromises = [];
      for (let tokenId = i; tokenId <= batchEndIndex; tokenId++) {
        batchPromises.push(getOwnerWithRetry(contract, tokenId));
      }
      
      // Process this batch
      const results = await Promise.all(batchPromises);
      
      // Track statistics
      const batchSuccess = results.filter(r => r.success).length;
      const batchFailed = results.filter(r => !r.success).length;
      
      successCount += batchSuccess;
      failedCount += batchFailed;
      processedCount += results.length;
      
      // Add successful results to our holders data
      results.forEach(result => {
        if (result.success && result.owner) {
          const { tokenId, owner } = result;
          newTokenOwners.push({ tokenId, owner });
          
          // Track unique holders and their token count
          if (newHolders[owner]) {
            newHolders[owner].count++;
            newHolders[owner].tokenIds.push(tokenId);
          } else {
            newHolders[owner] = { count: 1, tokenIds: [tokenId] };
          }
        }
      });
      
      // Update progress
      const progressPercent = Math.floor((processedCount / totalTokens) * 100);
      setProgress(progressPercent);
      addToLog(`Batch complete. Success: ${batchSuccess}, Failed: ${batchFailed}, Progress: ${progressPercent}%`);
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, retryDelay * 2));
    }
    
    addToLog(`Scan complete. Total tokens found: ${newTokenOwners.length}/${totalTokens}`);
    addToLog(`Unique holders found: ${Object.keys(newHolders).length}`);
    
    return { holders: newHolders, tokenOwners: newTokenOwners };
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate contract address
    if (!ethers.isAddress(contractAddress)) {
      setError('Please enter a valid contract address');
      return;
    }
    
    if (startTokenId < 0 || endTokenId < startTokenId) {
      setError('Invalid token ID range');
      return;
    }
    
    setLoading(true);
    setProgress(0);
    setError('');
    setHolders({});
    setTokenOwners([]);
    setContractInfo(null);
    setProcessLog([]);
    
    try {
      addToLog(`Starting scan for contract: ${contractAddress}`);
      addToLog(`Range: ${startTokenId} to ${endTokenId}`);
      
      const contract = await connectAndGetContract();
      const info = await getContractInfo(contract);
      setContractInfo(info);
      
      const { holders, tokenOwners } = await processTokensInBatches(contract);
      
      setHolders(holders);
      setTokenOwners(tokenOwners);
      setLastUpdated(new Date());
      setLoading(false);
      setProgress(100);
      
      addToLog("Scan completed successfully");
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || 'An error occurred while fetching holders');
      addToLog(`Error: ${error.message}`);
      setLoading(false);
    }
  };

  // Download CSV
  const downloadCSV = () => {
    // Create CSV for holders
    const holdersArray = Object.entries(holders).map(([address, data]) => ({
      address,
      count: data.count,
      tokenIds: data.tokenIds.join(',')
    }));
    
    const holdersCSV = 'Address,TokenCount,TokenIDs\n' + 
      holdersArray
        .sort((a, b) => b.count - a.count) // Sort by token count (descending)
        .map(({ address, count, tokenIds }) => `${address},${count},"${tokenIds}"`)
        .join('\n');
    
    const blob = new Blob([holdersCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${contractAddress}-holders.csv`);
    a.click();
    
    addToLog("CSV file exported");
  };

  // Render the holders table
  const renderHoldersTable = () => {
    const holdersArray = Object.entries(holders).map(([address, data]) => ({
      address,
      count: data.count,
      tokenIds: data.tokenIds
    }));
    
    const sortedHolders = holdersArray.sort((a, b) => b.count - a.count);
    
    return (
      <div>
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Tokens Held</th>
              <th>Token IDs</th>
            </tr>
          </thead>
          <tbody>
            {sortedHolders.map((holder, index) => (
              <tr key={holder.address}>
                <td>{index + 1}</td>
                <td>
                  {holder.address.substring(0, 6)}...{holder.address.substring(38)}
                </td>
                <td>{holder.count}</td>
                <td>
                  {holder.tokenIds.length > 5 
                    ? `${holder.tokenIds.slice(0, 5).join(', ')}... (${holder.tokenIds.length - 5} more)`
                    : holder.tokenIds.join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container">
      <header className="header">
        <h1>MegaETH NFT Holders Tool</h1>
        <p>Extract all holders for any ERC721 NFT contract on MegaETH</p>
      </header>

      <div className="grid">
        <div className="card">
          <h2 className="mb-4">Contract Information</h2>
          <div className="form-group">
            <label htmlFor="contractAddress">Contract Address</label>
            <input
              type="text"
              id="contractAddress"
              className="form-control"
              value={contractAddress}
              onChange={(e) => setContractAddress(e.target.value)}
              placeholder="0x..."
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="startTokenId">Start Token ID</label>
            <input
              type="number"
              id="startTokenId"
              className="form-control"
              value={startTokenId}
              onChange={(e) => setStartTokenId(parseInt(e.target.value))}
              min="0"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endTokenId">End Token ID</label>
            <input
              type="number"
              id="endTokenId"
              className="form-control"
              value={endTokenId}
              onChange={(e) => setEndTokenId(parseInt(e.target.value))}
              min={startTokenId}
              disabled={loading}
            />
            <div className="text-xs text-gray-400 mt-1">
              <p><strong>Important:</strong> For a 1000 token collection that starts at 0, use 0-999.</p>
            </div>
          </div>
          
          <button 
            onClick={() => setShowAdvanced(!showAdvanced)} 
            className="btn w-full mb-4"
          >
            {showAdvanced ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </button>
          
          {showAdvanced && (
            <div className="card mb-4">
              <h3 className="mb-2">Advanced Settings</h3>
              <div className="form-group">
                <label htmlFor="batchSize">Batch Size</label>
                <input
                  type="number"
                  id="batchSize"
                  className="form-control"
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value))}
                  min="1"
                  max="50"
                  disabled={loading}
                />
                <div className="text-xs text-gray-400 mt-1">
                  <p>Smaller batches (10-25) reduce errors but are slower.</p>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="retryDelay">Retry Delay (ms)</label>
                <input
                  type="number"
                  id="retryDelay"
                  className="form-control"
                  value={retryDelay}
                  onChange={(e) => setRetryDelay(parseInt(e.target.value))}
                  min="100"
                  max="5000"
                  step="100"
                  disabled={loading}
                />
                <div className="text-xs text-gray-400 mt-1">
                  <p>Longer delays (500-1000ms) reduce errors but are slower.</p>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="maxRetries">Max Retries</label>
                <input
                  type="number"
                  id="maxRetries"
                  className="form-control"
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                  min="1"
                  max="10"
                  disabled={loading}
                />
                <div className="text-xs text-gray-400 mt-1">
                  <p>More retries (3-5) increase success rate but are slower.</p>
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={handleSubmit}
            className={`btn ${loading ? 'btn-disabled' : 'btn-primary'} w-full`}
            disabled={loading}
          >
            {loading ? 'Fetching Holders...' : 'Fetch Holders'}
          </button>
          
          {loading && (
            <div className="mt-4">
              <div className="flex justify-between mb-1">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="alert alert-danger">
              <p>{error}</p>
            </div>
          )}
          
          {contractInfo && (
            <div className="mt-4 card">
              <h3 className="mb-2">Contract Details</h3>
              <div>
                <p>
                  <span className="text-gray-400">Name:</span>{" "}
                  <span>{contractInfo.name}</span>
                </p>
                <p>
                  <span className="text-gray-400">Symbol:</span>{" "}
                  <span>{contractInfo.symbol}</span>
                </p>
                <p>
                  <span className="text-gray-400">Total Supply:</span>{" "}
                  <span>
                    {contractInfo.totalSupply !== null 
                      ? contractInfo.totalSupply 
                      : "Unknown"}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex justify-between mb-4">
            <h2>Results Summary</h2>
            {Object.keys(holders).length > 0 && (
              <button
                onClick={downloadCSV}
                className="btn btn-primary"
              >
                Export CSV
              </button>
            )}
          </div>
          
          {Object.keys(holders).length > 0 ? (
            <>
              <div className="grid grid-cols-2">
                <div className="card">
                  <p className="text-gray-400">Unique Holders</p>
                  <p className="text-xl font-bold">{Object.keys(holders).length}</p>
                </div>
                <div className="card">
                  <p className="text-gray-400">Total Tokens</p>
                  <p className="text-xl font-bold">{tokenOwners.length}</p>
                </div>
              </div>
              
              {lastUpdated && (
                <p className="text-xs text-gray-400 mt-4">
                  Last updated: {lastUpdated.toLocaleString()}
                </p>
              )}
            </>
          ) : !loading && (
            <div className="text-center py-4">
              <p>No holder data yet</p>
              <p className="text-sm">Enter a contract address and click "Fetch Holders"</p>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex justify-between mb-4">
            <h2>Holders List</h2>
            <div className="text-sm">
              {Object.keys(holders).length > 0 && 
                `${Object.keys(holders).length} unique holders`}
            </div>
          </div>
          
          {Object.keys(holders).length > 0 ? (
            renderHoldersTable()
          ) : !loading && (
            <div className="text-center py-8">
              <p>Holders will appear here after fetching</p>
            </div>
          )}
        </div>
        
        <div className="card">
          <h2 className="mb-4">Process Log</h2>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {processLog.length > 0 ? (
              processLog.map((log, index) => (
                <div key={index} className="text-xs mb-1">
                  {log}
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p>Log will appear here during processing</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-6 text-center text-sm">
        <p>Made by <a href="https://twitter.com/RaveniumNFT" target="_blank" rel="noopener noreferrer" className="text-blue-400">@RaveniumNFT</a></p>
      </div>
    </div>
  );
}